import React, { useEffect, useState } from 'react';
import PageHeader from '../../PageHeader/PageHeader';
import { HitApi } from '../../Api/ApiHit';
import { searchPayment } from '../../constant/Constant';
import { useDispatch, useSelector } from 'react-redux';
import { setApiJson } from '../../redux/Actions/ApiAction';
import AppTable from '../../components/AppTable/AppTable';
import { setPagination } from '../../redux/Actions/TableAction';
import CustomModal from '../../components/CustomModal/CustumModal';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { CheckCircle, XCircle, Clock, RefreshCw, Eye } from 'lucide-react';
import TableSearch from '../../components/TableSearch/TableSearch';
import DateSearch from '../../components/DateSearch/DateSearch';

function PaymentMaster() {
  const dispatch = useDispatch();
  const ApiReducer = useSelector((state) => state.ApiReducer);
  const TableDataReducer = useSelector((state) => state.TableReducer);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [optionSelect, setOptionSelect] = useState('');
  const [valueSearched, setValueSearched] = useState('');

  useEffect(() => {
    loadPayments();
  }, [TableDataReducer?.pagination?.page]);

  const loadPayments = (searchQuery = {}) => {
    const json = {
      page: TableDataReducer?.pagination?.page || 1,
      limit: TableDataReducer?.pagination?.limit || 15,
      sort: { createdAt: -1 },
      search: searchQuery,
    };
    HitApi(json, searchPayment).then((res) => {
      if (res?.statusCode === 200) {
        const docs = Array.isArray(res?.data?.docs) ? res.data.docs : [];
        dispatch(setApiJson(docs));
        dispatch(setPagination({
          total: res?.data?.totalDocs || 0,
          page: TableDataReducer?.pagination?.page || 1,
          limit: TableDataReducer?.pagination?.limit || 15,
        }));
      } else {
        dispatch(setApiJson([]));
      }
    }).catch(() => {
      dispatch(setApiJson([]));
    });
  };

  const getStatusBadge = (status) => {
    const config = {
      TXN_SUCCESS: { bg: 'bg-green-100', text: 'text-green-700', icon: <CheckCircle className="w-3 h-3" />, label: 'Success' },
      TXN_FAILURE: { bg: 'bg-red-100', text: 'text-red-700', icon: <XCircle className="w-3 h-3" />, label: 'Failed' },
      INITIATED: { bg: 'bg-blue-100', text: 'text-blue-700', icon: <Clock className="w-3 h-3" />, label: 'Initiated' },
      PENDING: { bg: 'bg-yellow-100', text: 'text-yellow-700', icon: <Clock className="w-3 h-3" />, label: 'Pending' },
      REFUNDED: { bg: 'bg-purple-100', text: 'text-purple-700', icon: <RefreshCw className="w-3 h-3" />, label: 'Refunded' },
    }[status] || { bg: 'bg-gray-100', text: 'text-gray-700', icon: <Clock className="w-3 h-3" />, label: status || '—' };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
        {config.icon}
        {config.label}
      </span>
    );
  };

  const handleSearch = () => {
    if (optionSelect && valueSearched) {
      setIsSearching(true);
      const query = { [optionSelect]: { $regex: valueSearched, $options: 'i' } };
      dispatch(setPagination({ page: 1 }));
      loadPayments(query);
      setIsSearching(false);
    }
  };

  const handleClearSearch = () => {
    setOptionSelect('');
    setValueSearched('');
    setIsSearching(false);
    dispatch(setPagination({ page: 1 }));
    loadPayments();
  };

  const handleDateSearch = (startDate, endDate) => {
    if (startDate && endDate) {
      const query = {
        createdAt: {
          $gte: new Date(startDate).toISOString(),
          $lte: new Date(new Date(endDate).setHours(23, 59, 59, 999)).toISOString(),
        },
      };
      dispatch(setPagination({ page: 1 }));
      loadPayments(query);
    }
  };

  const baseIndex = ((TableDataReducer?.pagination?.page || 1) - 1) * (TableDataReducer?.pagination?.limit || 15);

  const tableHeaders = [
    {
      title: 'S.No',
      key: 'serialNumber',
      width: '60px',
      align: 'center',
      render: (_, __, index) => (
        <span className="text-sm font-medium text-gray-900">{baseIndex + index + 1}</span>
      ),
    },
    {
      title: 'Order ID',
      key: 'orderId',
      render: (value) => (
        <span className="text-xs font-mono font-medium text-gray-800">{value || '—'}</span>
      ),
    },
    {
      title: 'Client',
      key: 'clientInfo',
      render: (value) => (
        <div>
          <p className="text-sm font-medium text-gray-900">{value?.name || '—'}</p>
          {value?.phoneNumber && <p className="text-xs text-gray-500">{value.phoneNumber}</p>}
        </div>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      render: (value) => (
        <span className="text-sm font-semibold text-gray-900">
          ₹{Number(value || 0).toFixed(2)}
        </span>
      ),
    },
    {
      title: 'Status',
      key: 'paymentStatus',
      render: (value) => getStatusBadge(value),
    },
    {
      title: 'Payment Mode',
      key: 'paymentMode',
      render: (value) => (
        <span className="text-xs font-medium text-gray-600">{value || '—'}</span>
      ),
    },
    {
      title: 'Gateway',
      key: 'paymentGateway',
      render: (value) => (
        <span className="text-xs font-medium text-gray-600 capitalize">{value || '—'}</span>
      ),
    },
    {
      title: 'Remarks',
      key: 'remarks',
      render: (value) => (
        <span className="text-xs text-gray-500 truncate max-w-[200px] block">{value || '—'}</span>
      ),
    },
    {
      title: 'Date',
      key: 'createdAt',
      render: (value) => (
        <span className="text-xs text-gray-500">
          {value ? new Date(value).toLocaleDateString('en-IN', {
            day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
          }) : '—'}
        </span>
      ),
    },
    {
      title: 'Action',
      key: 'action',
      width: '70px',
      render: (_, row) => (
        <button
          onClick={() => { setSelectedPayment(row); setIsModalOpen(true); }}
          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
        >
          <Eye className="w-4 h-4" />
        </button>
      ),
    },
  ];

  const options = [
    { label: 'Order ID', value: 'orderId' },
    { label: 'Transaction ID', value: 'transactionId' },
    { label: 'Client ID', value: 'clientId' },
  ];

  return (
    <div className="p-4">
      <PageHeader title="Payments" />

      <AppTable
        TH={tableHeaders}
        TD={Array.isArray(ApiReducer.apiJson) ? ApiReducer.apiJson : []}
        isLoading={!Array.isArray(ApiReducer.apiJson) || ApiReducer.apiJson.length === 0 || isSearching}
        emptyMessage="No payments found"
        TableSearch={
          <TableSearch
            options={options}
            onClick={handleSearch}
            optionSelect={optionSelect}
            setOptionSelect={setOptionSelect}
            setValueSearched={setValueSearched}
            valueSearched={valueSearched}
            onClear={handleClearSearch}
          >
            <DateSearch onSearch={handleDateSearch} />
          </TableSearch>
        }
      />

      {/* Payment Detail Modal */}
      <CustomModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} width="max-w-lg">
        {selectedPayment && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Payment Details</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500">Status</span>
                {getStatusBadge(selectedPayment.paymentStatus)}
              </div>

              {selectedPayment.clientInfo && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Client</p>
                  <p className="text-sm font-medium">{selectedPayment.clientInfo.name}</p>
                  {selectedPayment.clientInfo.phoneNumber && (
                    <p className="text-xs text-gray-400">{selectedPayment.clientInfo.phoneNumber}</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Order ID</p>
                  <p className="text-sm font-mono font-medium">{selectedPayment.orderId}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Amount</p>
                  <p className="text-sm font-bold">₹{Number(selectedPayment.amount || 0).toFixed(2)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Payment Mode</p>
                  <p className="text-sm font-medium">{selectedPayment.paymentMode || '—'}</p>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Gateway</p>
                  <p className="text-sm font-medium capitalize">{selectedPayment.paymentGateway || '—'}</p>
                </div>
              </div>

              {selectedPayment.transactionId && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Transaction ID</p>
                  <p className="text-sm font-mono">{selectedPayment.transactionId}</p>
                </div>
              )}

              {selectedPayment.gatewayResponse && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500 mb-2">Gateway Response</p>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    {selectedPayment.gatewayResponse.bankName && (
                      <div><span className="text-gray-400">Bank:</span> <span className="font-medium">{selectedPayment.gatewayResponse.bankName}</span></div>
                    )}
                    {selectedPayment.gatewayResponse.bankTxnId && (
                      <div><span className="text-gray-400">Bank Txn:</span> <span className="font-mono">{selectedPayment.gatewayResponse.bankTxnId}</span></div>
                    )}
                    {selectedPayment.gatewayResponse.gatewayName && (
                      <div><span className="text-gray-400">Gateway:</span> <span className="font-medium">{selectedPayment.gatewayResponse.gatewayName}</span></div>
                    )}
                    {selectedPayment.gatewayResponse.respMsg && (
                      <div className="col-span-2"><span className="text-gray-400">Message:</span> <span className="font-medium">{selectedPayment.gatewayResponse.respMsg}</span></div>
                    )}
                    {selectedPayment.gatewayResponse.txnDate && (
                      <div><span className="text-gray-400">Txn Date:</span> <span className="font-medium">{selectedPayment.gatewayResponse.txnDate}</span></div>
                    )}
                  </div>
                </div>
              )}

              {selectedPayment.refundAmount > 0 && (
                <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                  <p className="text-xs text-purple-500">Refund</p>
                  <p className="text-sm font-bold text-purple-700">₹{Number(selectedPayment.refundAmount || 0).toFixed(2)}</p>
                  {selectedPayment.refundId && (
                    <p className="text-xs font-mono text-purple-500 mt-1">{selectedPayment.refundId}</p>
                  )}
                </div>
              )}

              {selectedPayment.remarks && (
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Remarks</p>
                  <p className="text-sm">{selectedPayment.remarks}</p>
                </div>
              )}

              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-500">Created At</p>
                <p className="text-sm">
                  {selectedPayment.createdAt
                    ? new Date(selectedPayment.createdAt).toLocaleDateString('en-IN', {
                        day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit'
                      })
                    : '—'}
                </p>
              </div>
            </div>
          </div>
        )}
      </CustomModal>
    </div>
  );
}

export default PaymentMaster;
